import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import OrderTable from "../../components/OrderManagement/OrderTable";
import { useFetchOrderDetails } from "../../hooks/useFetchOrderDetails";
import { useOrderCancellationActions } from "hooks/useOrderCancellationActions";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../widgets/Snackbar/SuccessSnackbar";
import {
  ORDER_CANCELLATION_REQUESTED_STATUS_ID,
  ORDER_CANCELLED_STATUS_ID,
} from "constants/constants";

const ACTION_LABEL = {
  accept: "accepted",
  reject: "rejected",
};

const EMPTY_ORDERS = [];

const OrderCancellation = () => {
  const { studentId } = useParams();
  const {
    data,
    isLoading,
    error: fetchError,
    refetch,
  } = useFetchOrderDetails(studentId);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [dismissedFetchError, setDismissedFetchError] = useState(null);
  const [pendingActionMap, setPendingActionMap] = useState({});

  const fetchErrorMsg =
    fetchError && dismissedFetchError !== fetchError
      ? `Failed to load order details. ${fetchError.message}`
      : "";
  const displayedErrorMsg = errorMsg || fetchErrorMsg;

  const ordersData = data?.data?.order_details || EMPTY_ORDERS;

  const filteredOrders = ordersData.filter(
    (order) =>
      order.status?.id === ORDER_CANCELLATION_REQUESTED_STATUS_ID ||
      order.status?.id === ORDER_CANCELLED_STATUS_ID,
  );

  const handleSuccess = useCallback(
    (action) => {
      setSuccessMsg(`Order cancellation ${ACTION_LABEL[action]} successfully.`);
      refetch();
    },
    [refetch],
  );

  const handleError = useCallback((action, order, message) => {
    setErrorMsg(`Failed to ${action} cancellation request. ${message}`);
  }, []);

  const { acceptCancellation, rejectCancellation, isOrderLoading } =
    useOrderCancellationActions({
      onSuccess: handleSuccess,
      onError: handleError,
    });

  const setPendingOrderAction = useCallback((orderId, action) => {
    setPendingActionMap((prev) => ({ ...prev, [orderId]: action }));
  }, []);

  const getOrderLoadingAction = useCallback(
    (orderId) => pendingActionMap[orderId] || isOrderLoading(orderId),
    [isOrderLoading, pendingActionMap],
  );

  const handleAccept = useCallback(
    async (order) => {
      setPendingOrderAction(order.order_id, "accept");

      try {
        await acceptCancellation(order);
      } finally {
        setPendingOrderAction(order.order_id, null);
      }
    },
    [acceptCancellation, setPendingOrderAction],
  );

  const handleReject = useCallback(
    async (order) => {
      setPendingOrderAction(order.order_id, "reject");

      try {
        await rejectCancellation(order);
      } finally {
        setPendingOrderAction(order.order_id, null);
      }
    },
    [rejectCancellation, setPendingOrderAction],
  );

  return (
    <>
      <OrderTable
        title="Order Cancellation"
        orders={filteredOrders}
        isLoading={isLoading}
        fetchError={fetchError}
        actionableStatusIds={[ORDER_CANCELLATION_REQUESTED_STATUS_ID]}
        isOrderLoading={getOrderLoadingAction}
        onAccept={handleAccept}
        onReject={handleReject}
      />

      <ErrorSnackbar
        open={!!displayedErrorMsg}
        message={displayedErrorMsg}
        onClose={() => {
          if (errorMsg) {
            setErrorMsg("");
            return;
          }

          setDismissedFetchError(fetchError);
        }}
      />

      <SuccessSnackbar
        open={!!successMsg}
        message={successMsg}
        onClose={() => setSuccessMsg("")}
      />
    </>
  );
};

export default OrderCancellation;
